import { makeAutoObservable, runInAction } from "mobx";
import { api } from "e.sic-platform.webclient/src/services/api/ApiService";
import { endpoints } from "e.sic-platform.webclient/src/services/api/ApiEndpoints";
import { snackbarNotificationsService } from "e.sic-platform.webclient/src/services/notifications/SnackbarNotificationsService";

export type DownloadStatus = "downloading" | "completed" | "failed" | "cancelled";

export interface DownloadItem {
  id: string;
  fileName: string;
  progress: number;
  loaded: number;
  total: number;
  status: DownloadStatus;
  abortController: AbortController | null;
}

class DownloadManagerService {
  downloads: DownloadItem[] = [];
  isExpanded: boolean = true;

  constructor() {
    makeAutoObservable(this);
  }

  get activeCount(): number {
    return this.downloads.filter(d => d.status === "downloading").length;
  }

  get hasDownloads(): boolean {
    return this.downloads.length > 0;
  }

  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  startBackupDownload(fileId: number, activityType: number, fileName: string) {
    const id = `${fileId}-${Date.now()}`;
    const abortController = new AbortController();

    const item: DownloadItem = {
      id,
      fileName,
      progress: 0,
      loaded: 0,
      total: 0,
      status: "downloading",
      abortController,
    };

    this.downloads.unshift(item);

    this.executeDownload(id, fileId, activityType, fileName, abortController.signal);
  }

  private async executeDownload(id: string, fileId: number, activityType: number, fileName: string, signal: AbortSignal) {
    let url: string | null = null;
    try {
      const endpoint = endpoints.get("media_file_export_encrypted");
      endpoint.routeParams = [fileId, activityType];

      const response = await api.download(endpoint, (progressEvent: any) => {
        runInAction(() => {
          const item = this.downloads.find(d => d.id === id);
          if (item) {
            item.loaded = progressEvent.loaded || 0;
            if (progressEvent.total) {
              item.total = progressEvent.total;
              item.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          }
        });
      }, signal);

      const blob = response.data;
      url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      url = null;

      runInAction(() => {
        const item = this.downloads.find(d => d.id === id);
        if (item) {
          item.status = "completed";
          item.progress = 100;
        }
      });

      snackbarNotificationsService.showSuccess("general.export.metadata.completed");
    } catch (error: any) {
      if (url) {
        URL.revokeObjectURL(url);
        url = null;
      }
      runInAction(() => {
        const item = this.downloads.find(d => d.id === id);
        if (item) {
          if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED" || signal.aborted) {
            item.status = "cancelled";
          } else {
            item.status = "failed";
            snackbarNotificationsService.showError("general.export.metadata.error");
          }
        }
      });
    }
  }

  cancelDownload(id: string) {
    const item = this.downloads.find(d => d.id === id);
    if (item && item.abortController) {
      item.abortController.abort();
      item.status = "cancelled";
    }
  }

  removeDownload(id: string) {
    this.downloads = this.downloads.filter(d => d.id !== id);
  }

  clearCompleted() {
    this.downloads = this.downloads.filter(d => d.status === "downloading");
  }
}

export const downloadManager = new DownloadManagerService();
